import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme,
  Grid,
  Alert,
  Tooltip,
  Button,
  Snackbar,
  Typography,
  Paper,
} from '@mui/material';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Table from '../../../common/components/Table/Table';
import {
  useE55FileDetails,
  useE55SamplesData,
  useAddE55Sample,
  useUpdateE55Sample,
  useE55Validate,
  useE55Finalize,
  useDeleteE55Sample,
  SortConfig,
  FilterConfig,
} from './hooks';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { useNavItems, usePermission } from '../../../hooks';
import { ContentPaper, MainContent, ActionBar } from './ViewSamples.styled';
import { ContextMenuAction, SortDirection } from '../../../common/components/Table/types';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { ValidationError } from '../../../hooks/useFileUpload';
import { ASIModal, E55EditModal } from './components';

// Custom hook for handling paginated errors
function usePaginatedErrors(errors: ValidationError[]) {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' } | null>(null);

  // Ensure errors are properly formatted and assign IDs
  const processedErrors = useMemo(() => {
    if (!Array.isArray(errors)) {
      console.error('Errors is not an array:', errors);
      return [];
    }
    
    const processed = errors.map((error, index) => {
      // Ensure error has all required fields or provide defaults
      if (!error || typeof error !== 'object') {
        console.warn(`Invalid error at index ${index}:`, error);
        return {
          id: `error-${index}`,
          sampleId: null,
          version: null,
          field: 'unknown',
          errorCode: 'UnknownError',
          message: 'An unknown error occurred'
        };
      }
      
      return {
        id: `error-${index}`, // Add id for table row key
        sampleId: error.sampleId ?? null,
        version: error.version ?? null,
        field: error.field || 'unknown',
        errorCode: error.errorCode || 'Error',
        message: error.message || 'No error message provided'
      };
    });

    // Apply sorting if configured
    if (sortConfig) {
      return [...processed].sort((a, b) => {
        const aValue = a[sortConfig.field as keyof typeof a];
        const bValue = b[sortConfig.field as keyof typeof b];
        
        if (aValue === null) return sortConfig.direction === 'ASC' ? -1 : 1;
        if (bValue === null) return sortConfig.direction === 'ASC' ? 1 : -1;
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ASC' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        // Default comparison for other types
        return sortConfig.direction === 'ASC' 
          ? (aValue < bValue ? -1 : 1) 
          : (aValue > bValue ? -1 : 1);
      });
    }
    
    return processed;
  }, [errors, sortConfig]);

  // Calculate total error count
  const totalErrorCount = processedErrors.length;

  // Get the paginated slice of errors for the current page
  const paginatedErrors = useMemo(() => {
    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    return processedErrors.slice(startIndex, endIndex);
  }, [processedErrors, currentPage, pageSize]);

  // Sort errors based on field and direction
  const sortErrors = useCallback((field: string, direction: 'ASC' | 'DESC') => {
    setSortConfig({ field, direction });
    setCurrentPage(0); // Reset to first page when sorting
  }, []);

  return {
    paginatedErrors,
    totalErrorCount,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    sortErrors
  };
}

// Custom Error Table Component that handles its own pagination
const PaginatedErrorTable: React.FC<{ errors: ValidationError[], fileName?: string }> = ({ errors, fileName }) => {
  const {
    paginatedErrors,
    totalErrorCount,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    sortErrors
  } = usePaginatedErrors(errors);

  // Define table columns with fixed widths to ensure alignment
  const columns = useMemo(() => [
    {
      Header: 'Sample ID',
      accessor: 'sampleId',
      width: '100px', // Fixed width to ensure alignment
      Cell: ({ value }: { value: string | null }) => (
        value !== null ? value : '-'
      )
    },
    {
      Header: 'Version',
      accessor: 'version',
      width: '100px', // Fixed width to ensure alignment
      Cell: ({ value }: { value: string | null }) => (
        value !== null ? value : '-'
      )
    },
    {
      Header: 'Field',
      accessor: 'field',
      width: '200px', // Fixed width to ensure alignment
    },
    {
      Header: 'Error Code',
      accessor: 'errorCode',
      width: '200px', // Fixed width to ensure alignment
    },
    {
      Header: 'Message',
      accessor: 'message',
      width: '500px', // Fixed width to ensure alignment
      Cell: ({ value }: { value: string }) => (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',  // Allow text to wrap to new lines
            wordBreak: 'break-word', // Break words to prevent overflow
            overflowWrap: 'break-word', // Ensure long words break and wrap
            lineHeight: 1.5,         // Increase line height for better readability
            padding: '8px 0',        // Add some padding for better spacing
          }}
          data-testid="error-message-cell"
        >
          {value}
        </Typography>
      )
    }
  ], []);

  // Handle sorting
  const handleSort = (column: string, direction: 'ASC' | 'DESC' | undefined, searchColumn?: string, searchTerm?: string) => {
    if (direction) {
      sortErrors(column, direction);
    }
  };

  // Early return if there are no valid errors
  if (totalErrorCount === 0) {
    return null;
  }

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        width: '100%', 
        mt: 2,
        overflow: 'hidden', // Prevents horizontal overflow
        '& .MuiTableContainer-root': {
          overflow: 'auto'
        },
        '& .MuiTable-root': {
          tableLayout: 'fixed' // Forces the table to respect column widths
        }
      }}
      data-testid="paginated-error-table"
    >
      <Table
        data={paginatedErrors}
        columns={columns}
        moduleName={`Validation Errors for ${fileName || 'file'} (${totalErrorCount})`}
        pageSize={pageSize}
        page={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        totalCount={totalErrorCount}
        onSort={handleSort}
        viewMode={true} // Set to true to hide action buttons
        isRowClickable={false}
        disableAutoSizing={true} // Disable auto-sizing to use our fixed widths
        data-testid="error-table"
      />
    </Paper>
  );
};

// Base interface for E55 data
interface E55BaseData {
  despatchSn: string;
  version: number;
  changeBy: string;
  comment: boolean;
  delFlag: number;
  validFlag: number;
  mailCategory: string;
  despatchMailSubclass: string;
  sendingPpo: string;
  oeFromCode: string;
  receivingPpo: string;
  oeToCode: string;
  despatchDate: string;
  despatchNumber: number;
  sampleStatus: string;
  pgeGross: number;
  pgeNet: number | null;
  weightOfMBagsUpTo5kg: number;
  numberOfMBagsUpTo5kg: number;
  weightOfMBagsOver5kg: number;
  vn: string;
  expressItems: number;
  cgross: number;
  cnet: number | null;
  egross: number;
  enet: number | null;
  ggross: number;
  gnet: number | null;
  pgross: number;
  pnet: number | null;
  ritems: number;
  vitems: number;
}

// Interface for existing E55 sample
interface E55Sample extends E55BaseData {
  id: number;
  despatchStatus: string;
}

// Interface for new E55 sample
interface NewE55Sample extends E55BaseData {
  isNew: true;
  id?: string;
  fileId?: number;
}

type E55SampleWithNew = E55Sample | NewE55Sample;

const ViewSamples: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fileId } = useParams<{ fileId: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navItems = useNavItems();
  const { hasPermission, isLoading: permissionLoading } = usePermission('/e55/despatches', '');
  const { hasPermission: hasEditPermission } = usePermission('/e55/despatches/edit', '');

  // Get pagination parameters from URL
  const page = parseInt(searchParams.get('page') || '0', 10);
  const pageSize = parseInt(searchParams.get('size') || '10', 10);
  const sortField = searchParams.get('sort') || '';
  const sortDirection = (searchParams.get('direction') || '') as 'ASC' | 'DESC';

  // Helper function to parse multiple sorts from URL
  const parseSortsFromURL = useCallback((params: URLSearchParams): SortConfig[] => {
    const sortParams = params.getAll('sort');
    const parsedSorts: SortConfig[] = [];
    
    sortParams.forEach((sortParam) => {
      const [column, direction] = sortParam.split(',');
      if (column && (direction === 'ASC' || direction === 'DESC')) {
        parsedSorts.push({ column, direction: direction as 'ASC' | 'DESC' });
      }
    });
    
    return parsedSorts;
  }, []);

  // Helper function to update URL with multiple sorts
  const updateSortsInURL = useCallback((newSorts: SortConfig[], params: URLSearchParams) => {
    // Remove all existing sort parameters
    params.delete('sort');
    
    // Add new sort parameters
    newSorts.forEach((sort) => {
      params.append('sort', `${sort.column},${sort.direction}`);
    });
  }, []);

  // Helper function to parse multiple filters from URL
  const parseFiltersFromURL = useCallback((params: URLSearchParams): FilterConfig[] => {
    const filters: FilterConfig[] = [];
    // Get all filter parameters
    const filterKeys = Array.from(params.keys()).filter(key => 
      key.startsWith('filter') && key.endsWith('Col')
    );
    
    filterKeys.forEach(key => {
      const colSuffix = key.replace('filter', '').replace('Col', '');
      const colValue = params.get(key);
      const termKey = `filter${colSuffix}Term`;
      const termValue = params.get(termKey);
      
      if (colValue && termValue) {
        filters.push({ column: colValue, term: termValue });
      }
    });
    
    // Also check for legacy single filter format
    const legacyCol = params.get('searchColumn');
    const legacyTerm = params.get('searchTerm');
    if (legacyCol && legacyTerm && !filters.some(f => f.column === legacyCol)) {
      filters.push({ column: legacyCol, term: legacyTerm });
    }
    
    return filters;
  }, []);

  // Helper function to update URL with multiple filters
  const updateFiltersInURL = useCallback((newFilters: FilterConfig[], params: URLSearchParams) => {
    // Remove all existing filter parameters
    const filterKeys = Array.from(params.keys()).filter(key => 
      key.startsWith('filter') || key === 'searchColumn' || key === 'searchTerm'
    );
    filterKeys.forEach(key => params.delete(key));
    
    // Add new filter parameters
    newFilters.forEach((filter, index) => {
      params.set(`filter${index}Col`, filter.column);
      params.set(`filter${index}Term`, filter.term);
    });
  }, []);

  // Check if edit mode is enabled via query parameter
  const isEditMode = searchParams.get('editMode') === 'true' && hasEditPermission;
  // Add explicit view mode flag
  const isViewMode = !isEditMode && hasPermission;

  const numericFileId = fileId ? parseInt(fileId, 10) : 0;
  const isValidFileId = !isNaN(numericFileId) && numericFileId > 0;

  // State to track the row being edited
  const [editingRowId, setEditingRowId] = useState<string | number | null>(null);

  // State for EditModal
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  // State to track if a new row is being added
  const [isAddingRow, setIsAddingRow] = useState<boolean>(false);

  // Store a temporary new row
  const [newRow, setNewRow] = useState<any>(null);

  // State for validation errors
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  // State for notification messages
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    type: 'info',
  });

  // Add state for button loading
  const [validateCorrectLoading, setValidateCorrectLoading] = useState<boolean>(false);
  const [validateFinalizeLoading, setValidateFinalizeLoading] = useState<boolean>(false);

  // Add state for ASI Modal
  const [isASIModalOpen, setIsASIModalOpen] = useState<boolean>(false);
  const [selectedASIRow, setSelectedASIRow] = useState<any | null>(null);

  // Add search state management
  const [searchColumn, setSearchColumn] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

  // Add status filter state management
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // State for multiple sorts
  const [sorts, setSorts] = useState<SortConfig[]>([]);
  
  // State for multiple filters
  const [filters, setFilters] = useState<FilterConfig[]>([]);

  // Mutation hooks for CRUD operations
  const addSampleMutation = useAddE55Sample();
  const updateSampleMutation = useUpdateE55Sample();
  const validateMutation = useE55Validate();
  const finalizeMutation = useE55Finalize();
  const deleteSampleMutation = useDeleteE55Sample();

  // Fetch file details
  const {
    data: fileDetails,
    isLoading: isFileDetailsLoading,
    isError: isFileDetailsError,
  } = useE55FileDetails({
    fileId: numericFileId,
    enabled: isValidFileId,
  });

  // Fetch samples data
  const {
    data: samplesData,
    isLoading: isSamplesLoading,
    isError: isSamplesError,
    error: samplesError,
    refetch: refetchSamples,
  } = useE55SamplesData({
    fileId: numericFileId,
    page,
    size: pageSize,
    enabled: isValidFileId,
    filters: filters.length > 0 ? filters : undefined,
    searchColumn: searchColumn, // Legacy support
    searchTerm: searchTerm, // Legacy support
    sorts: sorts.length > 0 ? sorts : undefined,
    sort: sortField, // Legacy support
    direction: sortDirection, // Legacy support
    status: statusFilter,
  }) as {
    data?: { content: E55Sample[]; totalElements: number };
    isLoading: boolean;
    isError: boolean;
    error: any;
    refetch: () => Promise<any>;
  };

  // Prepare data for the table
  const samplesContent = useMemo(() => {
    return (samplesData?.content || []) as E55Sample[];
  }, [samplesData]);

  // Check if file status allows validation actions
  const isValidateCorrectEnabled = useMemo(() => {
    // Enable if file status is DRAFT or D
    return fileDetails?.fileStatus === 'D' || fileDetails?.fileStatus === 'DRAFT';
  }, [fileDetails?.fileStatus]);

  const isValidateFinalizeEnabled = useMemo(() => {
    // Enable if file status does NOT have F or FINAL
    return fileDetails?.fileStatus !== 'F' && fileDetails?.fileStatus !== 'FINAL';
  }, [fileDetails?.fileStatus]);

  // Parse multiple sorts and filters from URL on mount and when URL changes
  useEffect(() => {
    const parsedSorts = parseSortsFromURL(searchParams);
    const parsedFilters = parseFiltersFromURL(searchParams);
    const statusParam = searchParams.get('status');
    
    // Only update if sorts actually changed to prevent infinite loops
    if (JSON.stringify(parsedSorts) !== JSON.stringify(sorts)) {
      setSorts(parsedSorts);
    }
    if (JSON.stringify(parsedFilters) !== JSON.stringify(filters)) {
      setFilters(parsedFilters);
    }
    setStatusFilter((prev) => {
      const newValue = statusParam || 'all';
      return prev !== newValue ? newValue : prev;
    });
  }, [searchParams, parseSortsFromURL, parseFiltersFromURL, sorts, filters]);

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/e55/despatches');
  };

  // Update URL when pagination parameters change
  const updateSearchParams = (params: Record<string, string>) => {
    const newSearchParams = new URLSearchParams(searchParams);

    // Update the search params with the new values
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        newSearchParams.set(key, value);
      } else {
        newSearchParams.delete(key);
      }
    });

    // Preserve edit mode parameter if it exists
    if (isEditMode) {
      newSearchParams.set('editMode', 'true');
    }

    // Update the URL without reloading the page
    setSearchParams(newSearchParams);
  };

  // Handle status filter change
  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    updateSearchParams({ status });
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    updateSearchParams({ page: newPage.toString() });
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    // First clear the size parameter to force a query parameter change
    const tempParams = new URLSearchParams(searchParams);
    tempParams.delete('size');
    tempParams.delete('page');
    setSearchParams(tempParams);

    // Then set it again with a slight delay to ensure URL changes are processed
    setTimeout(() => {
      updateSearchParams({
        page: '0', // Reset to first page when changing page size
        size: newSize.toString(),
      });
    }, 0);
  };

  // Handle sorting and searching with multi-sort and multi-filter support
  const handleSort = (column: string, direction: SortDirection | undefined, searchColumn?: string, searchTerm?: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    
    // Handle multiple sorts
    let newSorts: SortConfig[] = [...sorts];
    
    if (direction && direction !== 'undefined' as any) {
      // Check if this column is already in the sorts array
      const existingIndex = newSorts.findIndex((s) => s.column === column);
      
      if (existingIndex >= 0) {
        // Update existing sort direction
        newSorts[existingIndex].direction = direction;
      } else {
        // Add new sort
        newSorts.push({ column, direction });
      }
    } else {
      // Remove sort for this column if direction is undefined
      newSorts = newSorts.filter((s) => s.column !== column);
    }
    
    // Update sorts state
    setSorts(newSorts);
    
    // Update URL with new sorts
    updateSortsInURL(newSorts, newSearchParams);
    
    // Handle multiple filters
    let newFilters: FilterConfig[] = [...filters];
    
    if (searchColumn !== undefined) {
      if (searchTerm && searchTerm.trim()) {
        // Check if this column already has a filter
        const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
        
        if (existingFilterIndex >= 0) {
          // Update existing filter term
          newFilters[existingFilterIndex].term = searchTerm.trim();
        } else {
          // Add new filter
          newFilters.push({ column: searchColumn, term: searchTerm.trim() });
        }
      } else {
        // Remove filter for this column if searchTerm is empty or undefined
        newFilters = newFilters.filter((f) => f.column !== searchColumn);
      }
    }
    
    // Update filters state
    setFilters(newFilters);
    
    // Update URL with new filters
    updateFiltersInURL(newFilters, newSearchParams);
    
    // Update legacy state for backward compatibility
    setSearchColumn(searchColumn);
    setSearchTerm(searchTerm);
    
    // Preserve edit mode parameter if it exists
    if (isEditMode) {
      newSearchParams.set('editMode', 'true');
    }
    
    // Update the URL without reloading the page
    setSearchParams(newSearchParams);
  };

  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleCellEdit = (row: any, column: string, value: any) => {
    if (!isEditMode || !isEditAllowed(row)) return;

    const updatedRow = { ...row, [column]: value };
    setEditingRowId(updatedRow.id);
    setSelectedRow(updatedRow);
  };

  const handleRowSave = async (row: E55SampleWithNew) => {
    try {
      let response;
      
      if ('isNew' in row) {
        const { isNew, ...sampleData } = row;
        response = await addSampleMutation.mutateAsync({
          fileId: numericFileId,
          ...sampleData,
        } as any);

        // Clear the adding state
        setIsAddingRow(false);
        setNewRow(null);
      } else {
        response = await updateSampleMutation.mutateAsync({
          fileId: numericFileId,
          ...row,
        } as any);
      }

      // Check if the response contains a dataSetId and it differs from the current fileId
      // This applies to both new and existing samples
      if (response?.dataSetId && response.dataSetId.toString() !== fileId) {
        // Navigate to the correct file page
        navigate(
          `/e55/despatches/${response.dataSetId}${isEditMode ? '?editMode=true' : ''}`
        );
        return true;
      }
      if (response?.dataSetId && response.dataSetId.toString() === fileId) {
        // Navigate to the correct dataset page
        window.location.reload();
        return true;
      }

      await refetchSamples();
      setEditingRowId(null);
      setIsEditModalOpen(false);
      setNotification({
        open: true,
        message: 'isNew' in row ? 'Sample added successfully' : 'Sample updated successfully',
        type: 'success',
      });
      return true;
    } catch (error) {
      console.error('Error saving sample:', error);
      setNotification({
        open: true,
        message: 'Failed to save sample',
        type: 'error',
      });
      return false;
    }
  };

  const handleRowCancel = (row: any) => {
    setEditingRowId(null);
    setIsEditModalOpen(false);
  };

  const handleContextMenuAction = (action: ContextMenuAction, row: E55Sample) => {
    switch (action) {
      case 'edit':
      case 'editSample':
        if (isEditMode && isEditAllowed(row)) {
          setEditingRowId(row.id);
          // If sample status is Final, show comment as empty in edit mode
          const rowForEdit = row.sampleStatus === 'F' || row.despatchStatus === 'F'
            ? { ...row, comment: false }
            : row;
          setSelectedRow(rowForEdit);
          setIsEditModalOpen(true);
        }
        break;
      case 'delete':
        if (isEditMode && isEditAllowed(row)) {
          const rawId: any = (row as any).id ?? (row as any).sampleId ?? (row as any).despatchNumber;
          const sampleIdNum = typeof rawId === 'string' ? parseInt(rawId, 10) : Number(rawId);
          if (!Number.isFinite(sampleIdNum)) {
            setNotification({ open: true, message: 'Invalid sampleId for deletion', type: 'error' });
            break;
          }
          deleteSampleMutation.mutate(
            {
              fileId: numericFileId,
              sampleId: sampleIdNum,
            },
            {
              onSuccess: () => {
                refetchSamples();
                setNotification({
                  open: true,
                  message: 'Sample deleted successfully',
                  type: 'success',
                });
              },
              onError: () => {
                setNotification({
                  open: true,
                  message: 'Failed to delete sample',
                  type: 'error',
                });
              },
            }
          );
        }
        break;
      case 'deleteAmendment':
        if (isEditMode && row?.despatchStatus === 'D') {
          const sampleIdNum = Number(row?.despatchSn);
          if (Number.isNaN(sampleIdNum)) {
            setNotification({ open: true, message: 'Invalid sampleId for deletion', type: 'error' });
            break;
          }
          deleteSampleMutation.mutate(
            {
              fileId: numericFileId,
              sampleId: sampleIdNum,
            },
            {
              onSuccess: () => {
                refetchSamples();
                setNotification({
                  open: true,
                  message: 'Despatch deleted successfully',
                  type: 'success',
                });
              },
              onError: () => {
                setNotification({
                  open: true,
                  message: 'Failed to delete despatch',
                  type: 'error',
                });
              },
            }
          );
        }
        break;
      case 'additionalSampleInfo':
        // Implement additional sample information action logic here
        // This could open a modal with more detailed information
        setSelectedASIRow(row);
        setIsASIModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedRow(null);
    setEditingRowId(null);
    
    // If we were adding a new row but canceled, clear that state too
    if (isAddingRow) {
      setIsAddingRow(false);
      setNewRow(null);
    }
  };

  // Handle closing the ADI modal
  const handleCloseASIModal = () => {
    setIsASIModalOpen(false);
    setSelectedASIRow(null);
  };

  // Function to extract validation errors from API responses
  const extractValidationErrors = (errorResponse: any): ValidationError[] => {
    if (!errorResponse) return [];
    
    // Check common error response patterns
    if (errorResponse.errors && Array.isArray(errorResponse.errors)) {
      return errorResponse.errors;
    } else if (errorResponse.errorResponse && errorResponse.errorResponse.errors && 
               Array.isArray(errorResponse.errorResponse.errors)) {
      return errorResponse.errorResponse.errors;
    } else if (Array.isArray(errorResponse)) {
      return errorResponse;
    }
    
    return [];
  };
  
  // Reset validation errors
  const resetValidationErrors = () => {
    setValidationErrors([]);
  };

  const handleValidateAndCorrect = async () => {
    if (fileId) {
      setValidateCorrectLoading(true);
      // Clear any previous validation errors
      resetValidationErrors();
      
      try {
        await validateMutation.mutateAsync(numericFileId);

        setNotification({
          open: true,
          message: t(
            'e55.viewSamples.validateCorrectSuccess',
            'Validation and correction initiated successfully'
          ),
          type: 'success',
        });

        // Refresh the page by re-navigating to the current URL
        window.location.reload();
      } catch (error: any) {
        // Extract validation errors if they exist
        let errors: ValidationError[] = [];
        
        if (error.response?.data) {
          errors = extractValidationErrors(error.response.data);
        }
        
        if (errors.length > 0) {
          // Set validation errors to display in ErrorTable
          setValidationErrors(errors);
          
          // Show a general notification about validation errors
          setNotification({
            open: true,
            message: t(
              'e55.viewSamples.validationErrors',
              'Validation failed. Please review the errors below.'
            ),
            type: 'error',
          });
        } else {
          const errorMessage =
            error instanceof Error
              ? error.message
              : t(
                  'e55.viewSamples.validateCorrectError',
                  'Failed to initiate validation and correction'
                );

          setNotification({
            open: true,
            message: errorMessage,
            type: 'error',
          });
        }
      } finally {
        setValidateCorrectLoading(false);
      }
    }
  };

  const handleValidateAndFinalize = async () => {
    if (fileId) {
      setValidateFinalizeLoading(true);
      // Clear any previous validation errors
      resetValidationErrors();
      
      try {
        await finalizeMutation.mutateAsync(numericFileId);

        setNotification({
          open: true,
          message: t(
            'e55.viewSamples.validateFinalizeSuccess',
            'Validation and finalization initiated successfully'
          ),
          type: 'success',
        });

        // Navigate to /e55/despatches after showing the acceptance notification
        // Add a small delay to ensure the notification is seen
        setTimeout(() => {
          navigate('/e55/despatches');
        }, 1500);
      } catch (error: any) {
        // Extract validation errors if they exist
        let errors: ValidationError[] = [];
        
        if (error.response?.data) {
          errors = extractValidationErrors(error.response.data);
        }
        
        if (errors.length > 0) {
          // Set validation errors to display in ErrorTable
          setValidationErrors(errors);
          
          // Show a general notification about validation errors
          setNotification({
            open: true,
            message: t(
              'e55.viewSamples.finalizationErrors',
              'Finalization failed. Please review the errors below.'
            ),
            type: 'error',
          });
        } else {
          const errorMessage =
            error instanceof Error
              ? error.message
              : t(
                  'e55.viewSamples.validateFinalizeError',
                  'Failed to initiate validation and finalization'
                );

          setNotification({
            open: true,
            message: errorMessage,
            type: 'error',
          });
        }
      } finally {
        setValidateFinalizeLoading(false);
      }
    }
  };

  const isEditAllowed = (row: E55Sample): boolean => {
    return isEditMode && row.validFlag !== 0;
  };

  const handleAddRow = () => {
    const newSample: NewE55Sample = {
      isNew: true,
      despatchSn: '',
      version: 0,
      changeBy: '',
      comment: false,
      delFlag: 0,
      validFlag: 1,
      mailCategory: '',
      despatchMailSubclass: '',
      sendingPpo: fileDetails?.operatorByOutboundPpo || '',
      oeFromCode: '',
      receivingPpo: fileDetails?.operatorByInboundPpo || '',
      oeToCode: '',
      despatchDate: '',
      despatchNumber: 0,
      sampleStatus: 'D',
      pgeGross: 0,
      pgeNet: null,
      weightOfMBagsUpTo5kg: 0,
      numberOfMBagsUpTo5kg: 0,
      weightOfMBagsOver5kg: 0,
      vn: '',
      expressItems: 0,
      cgross: 0,
      cnet: null,
      egross: 0,
      enet: null,
      ggross: 0,
      gnet: null,
      pgross: 0,
      pnet: null,
      ritems: 0,
      vitems: 0,
      id: 'new-row-' + Date.now(),
      fileId: numericFileId,
    };
    setSelectedRow(newSample);
    setNewRow(newSample);
    setIsEditModalOpen(true);
    setIsAddingRow(true);
  };

  // Add this before getTableContent
  const customContextMenuActions = useMemo(() => {
    // For edit mode, return actions based on row status
    if (isEditMode) {
      return [
        {
          action: 'edit' as ContextMenuAction,
          label: t('e55.viewSamples.contextMenu.editDespatch', 'Edit Despatch'),
          icon: <EditIcon />,
        },
        {
          action: 'additionalSampleInfo' as ContextMenuAction,
          label: t('e55.viewSamples.contextMenu.asi', 'ADI (Additional Despatch Information)'),
          icon: <InfoOutlinedIcon />,
        },
        {
          action: 'deleteAmendment' as ContextMenuAction,
          label: t('e55.viewSamples.contextMenu.deleteAmendment', 'Delete Despatch'),
          icon: <DeleteIcon />,
          isVisible: (row: any) => row?.despatchStatus === 'D',
        },
      ];
    }
    // Options for view mode
    else if (isViewMode) {
      return [
        {
          action: 'additionalSampleInfo' as ContextMenuAction,
          label: t('e55.viewSamples.contextMenu.asi', 'ADI (Additional Despatch Information)'),
          icon: <InfoOutlinedIcon />,
        },
      ];
    }
    // Return empty array if neither edit nor view mode
    else {
      return [];
    }
  }, [isEditMode, isViewMode, t]);

  // Add columns definition before getTableContent
  const columns = useMemo(
    () => [
      {
        Header: 'despatch_sn',
        accessor: 'despatchSn',
        width: '120px',
      },
      {
        Header: 'ADI',
        accessor: 'adi',
        width: '70px',
        Cell: ({ value }: { value: any }) => {
          if (value === true) return 'Y';
          if (value === false) return '';
          return value !== null && value !== undefined ? String(value) : '';
        },
      },
      {
        Header: 'despatch_status',
        accessor: 'despatchStatus',
        width: '120px',
      },
      {
        Header: 'version',
        accessor: 'version',
        width: '100px',
      },
      {
        Header: 'change_by',
        accessor: 'changeBy',
        width: '120px',
      },
      {
        Header: 'comment',
        accessor: 'comment',
        width: '200px',
        Cell: ({ value }: { value: any }) => {
          if (value === true) return 'Y';
          if (value === false) return '';
          
          const commentText = value !== null && value !== undefined ? String(value) : '';
          
          if (!commentText) return '';
          
          // Truncate long comments (show first 50 characters)
          const maxLength = 50;
          const isLongComment = commentText.length > maxLength;
          const truncatedText = isLongComment 
            ? commentText.substring(0, maxLength) + '...' 
            : commentText;
          
          return (
            <Tooltip title={isLongComment ? commentText : ''} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '300px',
                  cursor: isLongComment ? 'help' : 'default',
                }}
              >
                {truncatedText}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        Header: 'del_flag',
        accessor: 'delFlag',
        width: '100px',
      },
      {
        Header: 'valid_flag',
        accessor: 'validFlag',
        width: '100px',
      },
      {
        Header: 'mail_category',
        accessor: 'mailCategory',
        width: '120px',
      },
      {
        Header: 'despatch_mail_subclass',
        accessor: 'despatchMailSubclass',
        width: '120px',
      },
      {
        Header: 'sending_ppo',
        accessor: 'sendingPpo',
        width: '120px',
      },
      {
        Header: 'oe_from_code',
        accessor: 'oeFromCode',
        width: '120px',
      },
      {
        Header: 'receiving_ppo',
        accessor: 'receivingPpo',
        width: '120px',
      },
      {
        Header: 'oe_to_code',
        accessor: 'oeToCode',
        width: '120px',
      },
      {
        Header: 'despatch_date',
        accessor: 'despatchDate',
        width: '150px',
      },
      {
        Header: 'despatch_number',
        accessor: 'despatchNumber',
        width: '130px',
      },
      
      {
        Header: 'p_gross',
        accessor: 'pgross',
        width: '100px',
      },
      {
        Header: 'p_net',
        accessor: 'pnet',
        width: '100px',
      },
      {
        Header: 'g_gross',
        accessor: 'ggross',
        width: '100px',
      },
      {
        Header: 'g_net',
        accessor: 'gnet',
        width: '100px',
      },
      {
        Header: 'e_gross',
        accessor: 'egross',
        width: '100px',
      },
      {
        Header: 'e_net',
        accessor: 'enet',
        width: '100px',
      },
      {
        Header: 'c_gross',
        accessor: 'cgross',
        width: '100px',
      },
      {
        Header: 'c_net',
        accessor: 'cnet',
        width: '100px',
      },
      {
        Header: 'pge_gross',
        accessor: 'pgeGross',
        width: '100px',
      },
      {
        Header: 'pge_net',
        accessor: 'pgeNet',
        width: '100px',
      },
      {
        Header: 'r_items',
        accessor: 'ritems',
        width: '100px',
      },
      {
        Header: 'v_items',
        accessor: 'vitems',
        width: '100px',
      },
      {
        Header: 'weight_of_m_bags_up_to_5kg',
        accessor: 'weightOfMBagsUpTo5kg',
        width: '150px',
      },
      {
        Header: 'number_of_m_bags_up_to_5kg',
        accessor: 'numberOfMBagsUpTo5kg',
        width: '150px',
      },
      {
        Header: 'weight_of_m_bags_over_5kg',
        accessor: 'weightOfMBagsOver5kg',
        width: '150px',
      },
      {
        Header: 'vn',
        accessor: 'vn',
        width: '100px',
      },
      {
        Header: 'express_items',
        accessor: 'expressItems',
        width: '120px',
      },
    ],
    []
  );

  const getTableContent = () => {
    if (isSamplesLoading || isFileDetailsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="view-samples-loading">
          <CircularProgress data-testid="view-samples-loading-spinner" />
        </Box>
      );
    }

    if (isSamplesError || isFileDetailsError) {
      return <Alert severity='error' data-testid="view-samples-error-alert">Error loading samples</Alert>;
    }

    if (!hasPermission) {
      return <Alert severity='warning' data-testid="view-samples-no-permission-alert">You don't have permission to view this section</Alert>;
    }

    return (
      <Box data-testid="view-samples-table-container">
        {/* Display validation errors if they exist */}
        {validationErrors.length > 0 && (
          <Box sx={{ mb: 3 }} data-testid="view-samples-validation-errors">
            <PaginatedErrorTable 
              errors={validationErrors} 
              fileName={`e55 File ID: ${fileId}`}
            />
          </Box>
        )}
        <Table
          data={samplesContent}
          columns={columns}
          page={page}
          pageSize={pageSize}
          totalCount={samplesData?.totalElements || 0}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSort}
          moduleName='e55 DESPATCHES'
          defaultSort={sorts.length > 0 ? sorts[0] : (sortField && sortDirection ? { column: sortField, direction: sortDirection } : undefined)}
          defaultSorts={sorts.length > 0 ? sorts : undefined}
          defaultFilters={filters.length > 0 ? filters : undefined}
          initialSearchColumn={searchColumn}
          initialSearchTerm={searchTerm}
          rowEventHandlers={{
            onContextMenuAction: handleContextMenuAction,
          }}
          viewMode={!isEditMode}
          editMode={isEditMode}
          onCellEdit={isEditMode ? handleCellEdit : undefined}
          onRowSave={isEditMode ? handleRowSave : undefined}
          onRowCancel={isEditMode ? handleRowCancel : undefined}
          readOnlyColumns={['id', 'version']}
          excludedColumns={[
            'despatchSn',
            'asi',
            'sampleStatus',
            'stratum',
            'receivedWeightStratum',
            'sendingPpo',
            'receivingPpo',
            'sampledWeightStratum',
            'effectiveSamplingRateStratum',
            'received_weight_stratum',
            'sampled_weight_stratum',
            'effective_sampling_rate_stratum',
            'receivedReceptaclesStratum',
            'received_receptacles_stratum',
          ]}
          requiredFields={[
            'version',
            'delFlag',
            'validFlag',
            'mailCategory',
            'formatCorrected',
            'samplingMethod',
            'netOrGrossAccounting',
            'samplingRate',
            'oeFromCode',
            'oeToCode',
            'samplingDate',
            'receptacleNumber',
            'despatchNumber',
            'despatchDate',
            'grossKg',
            'tareKg',
            'netKg',
            'pkg',
            'gkg',
            'ekg',
            'pitems',
            'gitems',
            'eitems',
            'despatchMailSubclass',
            'subsampleRate',
          ]}
          textAreaColumns={['comments']}
          dateColumns={['samplingDate', 'despatchDate', 'sampling_date', 'despatch_date']}
          defaultReadOnlyColumns={isAddingRow ? ['validFlag', 'delFlag', 'comments'] : ['validFlag']}
          priorityReadOnlyColumns={['sampleId', 'version', 'changeBy']}
          isRowEditable={isEditAllowed}
          hideDefaultActions={true}
          customActions={customContextMenuActions}
          data-testid="view-samples-table"
        />
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='view-samples-container'>
      <Grid container>
        <Grid item xs={12}>
          <SectionBar
            title={`e55 Weights - File ID:  ${fileId} - ${fileDetails?.fileName}${isEditMode ? ' (Edit Mode)' : ''}`}
            subtitle='e55 Message'
            onToggleSidebar={handleSidebarToggle}
            onSubtitleClick={handleSubtitleClick}
            isSidebarOpen={sidebarOpen}
            showFilters={{
              customStatus: true
            }}
            customFilters={{
              status: statusFilter,
              onStatusChange: handleStatusFilterChange
            }}
            data-testid="view-samples-section-bar"
          />
        </Grid>

        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="view-samples-sidebar" />
            </Box>
          )}

          <MainContent sidebarOpen={sidebarOpen} data-testid="view-samples-main-content">
            <ContentPaper data-testid="view-samples-content-paper">
              {isEditMode && (
                <ActionBar data-testid="view-samples-action-bar">
                  <Button
                    variant='contained'
                    color='secondary'
                    startIcon={!validateCorrectLoading && <VerifiedIcon />}
                    onClick={handleValidateAndCorrect}
                    disabled={validateCorrectLoading || !isValidateCorrectEnabled}
                    sx={{ mr: 1 }}
                    title={
                      !isValidateCorrectEnabled
                        ? t(
                            'e55.viewSamples.validateCorrectDisabled',
                            'Only drafts can be validated and corrected'
                          )
                        : ''
                    }
                    data-testid="view-samples-validate-correct-button"
                  >
                    {validateCorrectLoading ? (
                      <>
                        <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                        {t('e55.viewSamples.validating', 'Validating...')}
                      </>
                    ) : (
                      t('e55.viewSamples.validateCorrect', 'Validate & Correct')
                    )}
                  </Button>
                  <Button
                    variant='contained'
                    color='success'
                    startIcon={!validateFinalizeLoading && <DoneAllIcon />}
                    onClick={handleValidateAndFinalize}
                    disabled={validateFinalizeLoading || !isValidateFinalizeEnabled}
                    title={
                      !isValidateFinalizeEnabled
                        ? t('e55.viewSamples.validateFinalizeDisabled', 'Already finalized')
                        : ''
                    }
                    data-testid="view-samples-validate-finalize-button"
                  >
                    {validateFinalizeLoading ? (
                      <>
                        <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                        {t('e55.viewSamples.finalizing', 'Finalizing...')}
                      </>
                    ) : (
                      t('e55.viewSamples.validateFinalize', 'Validate & Finalize')
                    )}
                  </Button>
                  <Button
                    variant='contained'
                    sx={{ ml: 1 }}
                    color='primary'
                    startIcon={<AddIcon />}
                    onClick={handleAddRow}
                    fullWidth={isMobile}
                    data-testid="view-samples-add-row-button"
                  >
                    {t('e55.viewSamples.addSample', 'Add New Despatch')}
                  </Button>
                </ActionBar>
              )}
              {getTableContent()}
            </ContentPaper>
          </MainContent>

          {/* E55 Edit Modal */}
          {isEditMode && (
            <E55EditModal
              isOpen={isEditModalOpen}
              onClose={handleCloseEditModal}
              onSave={handleRowSave}
              excludedColumns={[
                'sampleStatus',
                'asi',
                'stratum',
                'receivedWeightStratum',
                'sendingPpo',
                'receivingPpo',
                'sampledWeightStratum',
                'effectiveSamplingRateStratum',
                'received_weight_stratum',
                'sampled_weight_stratum',
                'effective_sampling_rate_stratum',
                'receivedReceptaclesStratum',
                'received_receptacles_stratum',
              ]}
              requiredFields={[
                'mailCategory',
                'oeFromCode',
                'oeToCode'
              ]}
              textAreaColumns={['comment', 'comments']}
              dateColumns={['despatchDate']}
              defaultReadOnlyColumns={isAddingRow ? ['validFlag', 'delFlag'] : ['validFlag']}
              priorityReadOnlyColumns={['despatchSn', 'version', 'changeBy']}
              readOnlyColumns={['id', 'fileId', 'version']}
              row={selectedRow}
              columns={columns}
              title={
                isAddingRow
                  ? t('e55.viewSamples.addSample', 'Add New Despatch')
                  : t('e55.viewSamples.editSample', 'Edit Despatch')
              }
              data-testid="view-samples-edit-modal"
            />
          )}

          {/* ADI Modal for additional sample information */}
          <ASIModal
            isOpen={isASIModalOpen}
            onClose={handleCloseASIModal}
            row={selectedASIRow}
            dataSetId={numericFileId}
            fileDetails={fileDetails}
          />

          {/* Notification Snackbar */}
          <NotificationSnackbar
            open={notification.open}
            message={notification.message}
            severity={
              notification.type === 'success'
                ? 'success'
                : notification.type === 'error'
                  ? 'error'
                  : 'info'
            }
            onClose={handleNotificationClose}
            data-testid="view-samples-notification"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewSamples;
